import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { DateTimeRangePickerComponent } from './common/date-time-range-picker';
import { StateController } from '../helpers/state/state-management/state-controller';
import { getMainPage } from '../helpers/page-name';
import { palette } from '../style/variables';
import topicData from '../helpers/state/topic-data';

@customElement('date-time-range-modal')
export class DateTimeRangeModal extends LitElement {

    @query('#dateTimeRangePicker')
    private readonly dateTimeRangePickerComponentElement!: DateTimeRangePickerComponent;

    @state()
    filterType: string = '';

    private readonly bindTopicState = new StateController(this, topicData);

    firstUpdated() {
        this.autoSelectSingleOption();
    }

    // Also call it when topic data changes
    updated(changedProperties: Map<string | number | symbol, unknown>) {

        // Re-check auto-selection when topic data might have changed
        if (this.filterType === '') {
            this.autoSelectSingleOption();
        }
    }

    private toggleNow() {
        this.dateTimeRangePickerComponentElement.toggleNow();
        this.filterType = this.dateTimeRangePickerComponentElement.filterType;
    }

    private toggleToday() {
        this.dateTimeRangePickerComponentElement.toggleToday();
        this.filterType = this.dateTimeRangePickerComponentElement.filterType;
    }

    private toggleTomorrow() {
        this.dateTimeRangePickerComponentElement.toggleTomorrow();
        this.filterType = this.dateTimeRangePickerComponentElement.filterType;
    }

    private toggleThisWeek() {
        this.dateTimeRangePickerComponentElement.toggleThisWeek();
        this.filterType = this.dateTimeRangePickerComponentElement.filterType;
    }

    private toggleThisWeekend() {
        this.dateTimeRangePickerComponentElement.toggleThisWeekend();
        this.filterType = this.dateTimeRangePickerComponentElement.filterType;
    }

    private toggleOnSpecificDates() {
        this.dateTimeRangePickerComponentElement.toggleOnSpecificDates();
        this.filterType = this.dateTimeRangePickerComponentElement.filterType;
    }

    private toggleOnSpecificTimes() {
        this.dateTimeRangePickerComponentElement.toggleAtSpecificTime();
        this.filterType = this.dateTimeRangePickerComponentElement.filterType;
    }

    private clearAll() {
        this.dateTimeRangePickerComponentElement.clearAll();
        this.filterType = '';

        this.dispatchEvent(new CustomEvent('on-clear-datetime-filter'));
    }

    private applyFilter() {
        // get start date string if not there do today
        const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format

        const startDateString = this.dateTimeRangePickerComponentElement.startDateElement.value ?? today;
        const startTimeString =
            this.dateTimeRangePickerComponentElement.startTimeElement.value || '00:00';
        const endDateString = this.dateTimeRangePickerComponentElement.endDateElement.value ?? today;
        const endTimeString =
            this.dateTimeRangePickerComponentElement.endTimeElement.value || '23:59';
        const startString = startDateString
            ? `${startDateString}T${startTimeString}:00Z`
            : undefined;
        const endString = endDateString ? `${endDateString}T${endTimeString}:00Z` : undefined;

        if (
            startString !== undefined &&
            endString !== undefined &&
            new Date(startString).getTime() > new Date(endString).getTime()
        ) {
            getMainPage().errorToastElement.showToastAndThrow(
                'Please fix your invalid inputs (the start date occurs after the end date).'
            );
        }

        this.dispatchEvent(
            new CustomEvent('on-filter', {
                detail: {
                    startString,
                    endString,
                },
            })
        );
    }

    get isSingleOption() {
        const availableOptions = this.getAvailableFilterOptions();
        return availableOptions.length === 1;
    }

    private autoSelectSingleOption() {
        const availableOptions = this.getAvailableFilterOptions();

        if (availableOptions.length === 1) {
            const singleOption = availableOptions[0];

            // Auto-select the single available option
            switch (singleOption) {
                case 'now':
                    this.toggleNow();
                    break;
                case 'today':
                    this.toggleToday();
                    break;
                case 'tomorrow':
                    this.toggleTomorrow();
                    break;
                case 'thisWeek':
                    this.toggleThisWeek();
                    break;
                case 'thisWeekend':
                    this.toggleThisWeekend();
                    break;
                case 'specificDates':
                    this.toggleOnSpecificDates();
                    break;
                case 'specificTimes':
                    this.toggleOnSpecificTimes();
                    break;
            }
        }
    }

    // Helper method to determine which options are available
    private getAvailableFilterOptions(): string[] {
        const options: string[] = [];

        // Check if "Happening Now" is available
        if (topicData.topic?.show_easy_date_filters !== false) {
            options.push('now');
        }

        if (topicData.topic?.has_time_only !== true) {
            // Date-based options
            if (topicData.topic?.show_easy_date_filters !== false) {
                options.push('today', 'tomorrow', 'thisWeek', 'thisWeekend');
            }
            options.push('specificDates');
        } else {
            // Time-only mode
            options.push('specificTimes');
        }

        return options;
    }


    render() {
        return html`
            <div class="dateTimePickerModalContent">
                ${this.isSingleOption ? html`` : html`
                    <div class="nowButtonWrapper">
                        ${topicData.topic?.show_easy_date_filters !== false ? html`
                            <div class="dateOption ${this.filterType === 'now' ? 'selected' : ''}" @click="${this.toggleNow}">
                                Happening Now
                            </div>
                        ` : ''}
                        ${topicData.topic?.has_time_only !== true ? html`
                            ${topicData.topic?.show_easy_date_filters !== false ? html`
                                <div class="dateOption ${this.filterType === 'today' ? 'selected' : ''}" @click="${this.toggleToday}"><translated-span>Today</translated-span></div>
                                <div class="dateOption ${this.filterType === 'tomorrow' ? 'selected' : ''}" @click="${this.toggleTomorrow}"><translated-span>Tomorrow</translated-span></div>
                                <div class="dateOption ${this.filterType === 'thisWeek' ? 'selected' : ''}" @click="${this.toggleThisWeek}"><translated-span>This Week</translated-span></div>
                                <div class="dateOption ${this.filterType === 'thisWeekend' ? 'selected' : ''}" @click="${this.toggleThisWeekend}"><translated-span>This Weekend</translated-span></div>
                                ` : ''}
                            <div class="dateOption ${this.filterType === 'specificDates' ? 'selected' : ''}" @click="${this.toggleOnSpecificDates}"><translated-span>On Specific Dates</translated-span></div>
                        ` : html`
                            <div class="dateOption ${this.filterType === 'specificTimes' ? 'selected' : ''}" @click="${this.toggleOnSpecificTimes}"><translated-span>At Specific Times</translated-span></div>
                        `}
                    </div>
                `}
                <date-time-range-picker
                    id="dateTimeRangePicker"
                    .dateOnly=${topicData.topic?.has_date_only === true}
                    .timeOnly=${topicData.topic?.has_time_only === true}
                    .customHeader=${topicData.topic?.add_point_customization?.date_time?.header}
                    .isFilterView=${true}
                    ?filterPast=${topicData.topic?.should_filter_past_events}
                    ?clearEnabled=${true}
                >
                </date-time-range-picker>
                <div class="actionButtonWrapper">
                    <form-button size="sm" class="button save" @click="${this.applyFilter}"
                        ><translated-span>Apply</translated-span></form-button
                    >
                    <form-button size="sm" @click="${this.clearAll}"><translated-span>Clear</translated-span></form-button>
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .flexContainer {
                display: flex;
                align-items: center;
            }

            .dateTimePickerModalContent {
                padding: 0.5rem 2rem 0 1rem;
                --mdc-icon-size: 18px;
            }

            .dateOption {
                margin-right: 0.25rem;
                margin-bottom: 0.25rem;
                cursor: pointer;
                border-radius: 20px;
                border: 1px solid var(--theme-input-border-color);
                padding: 0.1rem 0.5rem;
            }

            .nowButtonWrapper {
                display: flex;
                margin: 1rem 0;
                flex-wrap: wrap;
                font-size: 0.875rem;
            }

            .dateOption:hover {
                background: var(--main-secondary-color);
            }

            .dateOption.selected {
                background: var(--main-primary-color);
                color: var(--theme-white-color);
            }

            .actionButtonWrapper {
                display: flex;
                justify-content: flex-end;
                margin-top: 1.5rem;
                margin-bottom: 0.5rem;
            }

            .nowButtonWrapper {
                display: flex;
                margin: 1rem 0;
            }

            .buttonDivider {
                width: 1px;
                height: 20px;
                margin: 0 0.5rem;
                background: var(--theme-white-color);
                opacity: 0.2;
            }

            .button.save {
                --btn-background-color: ${unsafeCSS(palette.successGreen)};
                margin-right: 0.5rem;
            }

            .modalTitle {
                font-size: 0.8rem;
                font-weight: 700;
                margin-left: 0.5rem;
            }
        `;
    }
}
